import { useEffect, useState } from 'react'
import { Alert } from '../../components/ui'
import PdfStamper from './PdfStamper'

/**
 * 一般用印：每個人蓋自己的印章。
 *
 * 印章存在瀏覽器本機（localStorage），不上傳伺服器——印鑑集中放在資料庫，
 * 等於帳號被盜就是印章被盜；存本機的話換電腦要重傳，但風險小得多。
 */

const SEAL_KEY = 'winho.seal.mine'
/** localStorage 單一網域大約只有 5MB，印章圖留 2MB 上限已經很寬鬆 */
const MAX_DATAURL = 2 * 1024 * 1024

/**
 * 檢查上傳的圖有沒有去背。
 *
 * 白底圖和透明圖放在白色頁面上長得一模一樣，蓋下去當下也看不出問題——
 * 要等文件寄出、對方打開發現簽名被白框蓋住才會知道。所以上傳時就先算一次：
 * 統計「不透明的白色像素」佔多少，比例高就是沒去背。
 */
function analyzeTransparency(dataUrl: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const c = document.createElement('canvas')
      c.width = img.naturalWidth
      c.height = img.naturalHeight
      const ctx = c.getContext('2d')
      if (!ctx) return resolve(0)
      ctx.drawImage(img, 0, 0)
      const d = ctx.getImageData(0, 0, c.width, c.height).data
      let opaqueWhite = 0
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] > 200 && d[i] > 230 && d[i + 1] > 230 && d[i + 2] > 230) opaqueWhite++
      }
      resolve((opaqueWhite / (c.width * c.height)) * 100)
    }
    img.onerror = () => resolve(0)
    img.src = dataUrl
  })
}

export default function GeneralSeal() {
  const [seal, setSeal] = useState<string | null>(null)
  const [warn, setWarn] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    setSeal(localStorage.getItem(SEAL_KEY))
  }, [])

  const onPick = async (file: File | undefined) => {
    if (!file) return
    setError(''); setWarn('')
    if (!/^image\/(png|webp)$/.test(file.type)) {
      setError('請上傳 PNG 或 WebP。JPG 不支援透明背景，蓋上去會有白底遮住文件內容。')
      return
    }
    const dataUrl = await new Promise<string>((res, rej) => {
      const r = new FileReader()
      r.onload = () => res(String(r.result))
      r.onerror = rej
      r.readAsDataURL(file)
    })
    if (dataUrl.length > MAX_DATAURL) {
      setError('圖檔太大（超過 2MB）。印章不需要那麼高的解析度，請先縮小再上傳。')
      return
    }
    const opaqueWhitePct = await analyzeTransparency(dataUrl)
    if (opaqueWhitePct > 5) {
      setWarn(
        `這張圖有 ${opaqueWhitePct.toFixed(0)}% 是不透明的白色，看起來沒有去背。` +
        '蓋到 PDF 上會像貼一張白紙，把底下的文字蓋住。仍可使用，但建議換一張去背過的 PNG。'
      )
    }
    try {
      localStorage.setItem(SEAL_KEY, dataUrl)
      setSeal(dataUrl)
    } catch {
      setError('存不進瀏覽器（空間不足）。請換一張更小的圖。')
    }
  }

  const clear = () => {
    localStorage.removeItem(SEAL_KEY)
    setSeal(null)
    setWarn('')
  }

  /* 印章上傳區塊，塞進 PdfStamper 側欄的最上方 */
  const uploader = (
    <div className="sp-block">
      <span className="sp-label">我的印章</span>
      {seal ? (
        <>
          {/* 棋盤格底：白底圖和透明圖在白色背景上看起來一樣，只有這樣才分得出來 */}
          <div className="seal-preview"><img src={seal} alt="我的印章" /></div>
          <div className="sp-row">
            <label className="sp-filebtn">
              更換
              <input type="file" accept="image/png,image/webp"
                onChange={(e) => onPick(e.target.files?.[0])} style={{ display: 'none' }} />
            </label>
            <button onClick={clear}>移除</button>
          </div>
        </>
      ) : (
        <label className="sp-filebtn">
          上傳印章圖
          <input type="file" accept="image/png,image/webp"
            onChange={(e) => onPick(e.target.files?.[0])} style={{ display: 'none' }} />
        </label>
      )}
    </div>
  )

  return (
    <PdfStamper
      title="一般用印"
      hint="蓋自己的印章"
      alerts={
        <>
          {error && <Alert kind="error">{error}</Alert>}
          {warn && <Alert kind="warn">{warn}</Alert>}
        </>
      }
      sealSrc={seal}
      defaultWidthMm={25}
      rememberKey="winho.seal.pos.general"
      sidebarTop={uploader}
      sealHint={
        <Alert kind="warn">
          還沒有印章。請先在左邊上傳一張<strong>去背過</strong>的 PNG（印文以外要是透明的），
          印章只存在這台電腦，不會上傳到伺服器。
        </Alert>
      }
    />
  )
}
